/* eslint-disable @next/next/no-html-link-for-pages */
import { useGoogleLogin } from "@react-oauth/google";

import Apple from "@web/public/apple.svg";
import Google from "@web/public/google.svg";
import TodayIcon from "@web/public/to-day.svg";
import { Button } from "@web/src/app/components/button";
import { REFERRAL_URL_PARAM } from "@web/src/app/components/modals/referrals-modal";
import { analytics } from "@web/src/app/use-cases/analytics";
import { makeDeeplink } from "@web/src/app/use-cases/deeplink";
import { handleWebError } from "@web/src/app/utils/web-errors";
import { useStores } from "@web/src/hooks/useStore";
import { redirectWithParams } from "@web/src/utils/redirect";
import { observer } from "mobx-react";
import dynamic from "next/dynamic";
import Image from "next/image";
import Link from "next/link";
import { useSearchParams } from "next/navigation";
import type { FC } from "react";
import { useEffect, useState } from "react";

interface LoginPageProps {}

const LoginPage: FC<LoginPageProps> = observer(({}) => {
  const { userStore } = useStores();

  const [isOpenInDesktop, setIsOpenInDesktop] = useState(false);
  const [didRedirectDeeplink, setDidRedirectDeeplink] = useState(false); // combat react strict mode

  const searchParams = useSearchParams();
  const fromParam = searchParams?.get("from");

  const deeplink = makeDeeplink(userStore.userProfile, userStore.deps.getAccessToken());
  useEffect(() => {
    if (userStore.isLoggedIn) {
      if (fromParam === "desktop") {
        if (!deeplink) throw new Error("Deeplink could not be generated");
        if (!didRedirectDeeplink) {
          window.location.href = deeplink;
        }
        setIsOpenInDesktop(true);
        setDidRedirectDeeplink(true);
        return;
      }

      redirectWithParams("/space");
    }
  }, [fromParam, deeplink, didRedirectDeeplink, userStore.isLoggedIn]);

  const onAppleLogin = async (authorizationCode: string, fullName: string | undefined) => {
    const referral = searchParams?.get(REFERRAL_URL_PARAM) ?? undefined;
    const success = await userStore.loginWithApple(authorizationCode, fullName, referral, "web");
    if (!success) return;
    if (fromParam === "desktop") return;
    redirectWithParams("/space");
  };

  const loginGoogle = useGoogleLogin({
    onSuccess: async ({ code }) => {
      const referral = searchParams?.get(REFERRAL_URL_PARAM) ?? undefined;
      const success = await userStore.loginWithGoogleAuthCode(code, referral, "web");
      if (!success) return;
      if (fromParam === "desktop") return;
      redirectWithParams("/space");
    },
    flow: "auth-code",
    // Don't ask for calendar scopes here, we'll do it when user connects. This is to skip "unverified" warning.
    // scope: "https://www.googleapis.com/auth/calendar.events https://www.googleapis.com/auth/calendar.readonly",
  });

  const isMobile = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);

  if (isMobile) {
    const isIOS = /iPhone|iPad|iPod/i.test(navigator.userAgent);
    const isAndroid = /Android/i.test(navigator.userAgent);

    if (isIOS) {
      window.open("https://apps.apple.com/sg/app/to-day-todo-app/id6657984041", "_blank");
    } else if (isAndroid) {
      window.open("https://play.google.com/store/apps/details?id=com.moosedev.to_day_mobile", "_blank");
    }

    return (
      <div className="h-screen w-full flex justify-center items-center">
        <div className="flex flex-col items-center gap-3 w-72 mb-10">
          <div className="flex flex-row gap-2">
            <TodayIcon className="w-7 h-7" />
            <div className="text-xl font-bold">To-day</div>
          </div>
          <button onClick={() => window.open("https://apps.apple.com/sg/app/to-day-todo-app/id6657984041")}>
            <Image width="120" height="40" src="/download_on_appstore.svg" alt="Download on the App Store" />
          </button>
          <button
            onClick={() => window.open("https://play.google.com/store/apps/details?id=com.moosedev.to_day_mobile")}
          >
            <Image src="/download_on_playstore.svg" width="120" height="40" alt="Download on the PlayStore" />
          </button>
        </div>
      </div>
    );
  }

  if (isOpenInDesktop) {
    return (
      <div className="h-screen w-full flex justify-center items-center">
        <div className="flex flex-col items-center gap-3 w-72 mb-10">
          <div className="flex flex-row gap-2">
            <TodayIcon className="w-7 h-7" />
            <div className="text-xl font-bold">To-day</div>
          </div>
          <h3 className="text-3xl whitespace-nowrap">Opened in Desktop</h3>
          {deeplink && (
            <p className="text-glyph-scd text-center text-xs">
              You can close this window and continue using To-day in the desktop app. If it failed, click{" "}
              <a className="text-blue-3 underline" href={deeplink}>
                here
              </a>{" "}
              to try again
            </p>
          )}
          {!deeplink && (
            <p className="text-red-3">Login failed. Please login again in the web browser and try again.</p>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="h-screen w-full flex justify-center items-center">
      <div className="flex flex-col items-center gap-1 w-72 mb-10">
        <div className="flex flex-row gap-2">
          <TodayIcon className="w-7 h-7" />
          <div className="text-xl font-bold">To-day</div>
        </div>
        <div>
          <h1 className="text-3xl font-bold mb-5 mt-1">Sign In</h1>
        </div>
        <div className="flex flex-col items-center gap-2 w-64">
          <Button
            onClick={() => {
              analytics.track("google_login_click");
              loginGoogle();
            }}
            className="flex items-center gap-4 rounded-lg shadow-1 py-[10px] px-4 w-full"
            mode="contrast"
            variant="solid"
          >
            <Google className="w-5 h-5" />
            <div className="flex-1 text-center font-semibold text-[16px]">Continue with Google</div>
          </Button>
          <Button
            onClick={async () => {
              const nonce = crypto.randomUUID();
              // For development, Apple redirect does not allow localhost?
              const domain =
                process.env.NODE_ENV === "development" ? "https://staging.to-day.app" : window.location.origin;
              const redirectURI = `${domain}/auth/callback?provider=apple`;
              await AppleID.auth.init({
                clientId: "com.moosedev.to-day-web",
                scope: "name email",
                nonce: nonce,
                redirectURI,
                state: nonce,
                usePopup: true,
              });

              const {
                authorization: { code, state },
                user,
              } = await AppleID.auth.signIn();

              if (state !== nonce) {
                handleWebError(new Error("Nonce mismatch. Please refresh and try again."));
                return;
              }

              await onAppleLogin(code, user.name.firstName + " " + user.name.lastName);
            }}
            className="flex items-center gap-4 rounded-lg shadow-1 py-[10px] px-4 w-full"
            mode="contrast"
            variant="solid"
            leadingIcon={<Apple className="w-5 h-5 text-glyph-cst-prm" color="white" />}
          >
            <div className="flex-1 text-center font-semibold text-[16px]">Continue with Apple</div>
          </Button>
        </div>
        <div className="min-w-0 flex flex-col mt-6">
          <p
            className="text-[10px] leading-[10px] text-glyph-scd text-center"
            style={{ fontFamily: "Arial", fontWeight: "lighter" }}
          >
            By clicking &quot;Continue&quot; above, you acknowledge that you have read and understood, and agree to
            To-day&apos;s{" "}
            <Link href="/terms" className="underline">
              Terms & Conditions
            </Link>{" "}
            and{" "}
            <a href="privacy" className="underline">
              Privacy Policy
            </a>
          </p>
        </div>
      </div>
    </div>
  );
});

export default dynamic(() => Promise.resolve(LoginPage), {
  ssr: false,
});
